import type { Tutorial } from '../../shared/types.js';

export const orgModelProvidersTutorial: Tutorial = {
  name: 'org-model-providers',
  title: 'Add an org model provider key',
  description:
    'Add an organization-wide model provider API key. This is the shared key that every agent uses as a default when a user has no private access keys of their own.',
  category: 'model-providers',
  docPages: ['administration/agent-model-configuration'],
  agenda: ['Add a provider key', 'Pick the provider and name it', 'Paste the key and save'],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'Organization model providers',
      body: 'These keys are org-wide fallbacks: agents launched for this organization use them whenever a user has no personal or team keys available for a provider. Set the keys once and everyone is covered.',
      path: '/configure/connect/credentials/organization/model-providers',
    },
    {
      id: 'add-key-open',
      kind: 'action',
      trigger: 'click',
      title: 'Add a key',
      body: 'Click **Add key** to open the dialog. Each provider can hold several named keys, so you can keep, for example, a billing key and a fallback key side by side. The tour will continue once the dialog opens.',
      target: '[data-tutorial-id="model-provider-add-key"]',
      placement: 'bottom',
    },
    {
      id: 'provider',
      kind: 'info',
      title: 'Pick the provider',
      body: 'Choose which provider this key is for. The menu lists every provider the org supports, such as Anthropic, OpenAI, or Google; the key you paste must belong to the one you select here.',
      target: '#model-provider-select',
      placement: 'bottom',
    },
    {
      id: 'name',
      kind: 'action',
      trigger: 'input',
      title: 'Name the key',
      body: 'Give the key a short, recognizable name. The name is how it appears in the launch dialog and in this list, so you can tell multiple keys for the same provider apart. Type one, or use **Fill example**, to continue.',
      example: 'billing',
      target: '#model-provider-name',
      placement: 'bottom',
    },
    {
      id: 'value',
      kind: 'info',
      title: 'Paste the API key',
      body: 'Paste the provider API key here. The key is encrypted the moment you save it and is never shown again. Check the key for accuracy using the show key button before saving.',
      target: '#model-provider-value',
      placement: 'top',
    },
    {
      id: 'save',
      kind: 'info',
      title: 'Save it',
      body: 'Click **Save** to store the key. It becomes the organization fallback for new launches. Each agent will resolve and mirror the key to the per-org gateway on agent startup. Agents launched after the change pick up the key automatically with no redeploy needed.',
      target: '[data-tutorial-id="model-provider-save"]',
      placement: 'top',
    },
    {
      id: 'done',
      kind: 'info',
      title: "That's org keys",
      body: "Org keys complete the chain of precedence. Agents will try a user's personal key first, then any team credentials. Finally, the org-wide credentials are used as a fallback. Personal and team keys are managed from the Credentials page in **Connect** and feed the same dialog you just saw.",
    },
  ],
};
